/**
 * Owner "Email alerts" settings (1.4 P03) — one switch per business alert the
 * platform emails the owner, grouped as the backend catalog groups them, plus
 * the master switch (owners.alert_channels.email). Shared by the owner route
 * and the admin owner-twin: admins pass makeOwnerApi(ownerId) so every call
 * carries ?ownerId=.
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Switch, ActivityIndicator } from 'react-native';
import { useFocusEffect } from 'expo-router';

import Card from '../components/Card';
import ContentContainer from '../components/ContentContainer';
import { useToast } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight } from '../utils/theme';

export interface EmailAlertsApiClient {
  get<T = unknown>(url: string): Promise<T>;
  put<T = unknown>(url: string, body?: unknown): Promise<T>;
}

interface CatalogEntry { key: string; label: string; description: string; group: string }
interface EmailAlertsResponse { masterEmail: boolean; prefs: Record<string, boolean>; catalog: CatalogEntry[] }

interface Props {
  client: EmailAlertsApiClient;
  /** Set on the admin twin (reserved for owner-only affordances). */
  adminOwnerId?: string;
}

export default function EmailAlertsScreen({ client }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [master, setMaster] = useState(true);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await client.get<EmailAlertsResponse>('/api/owner/email-alerts');
      setMaster(data.masterEmail);
      setPrefs(data.prefs);
      setCatalog(data.catalog);
    } catch (e: any) {
      setError(e?.message || 'Could not load email alert settings');
    } finally {
      setLoading(false);
    }
  }, [client]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const saveMaster = async (value: boolean) => {
    const prev = master;
    setMaster(value);
    setSaving('master');
    try {
      await client.put('/api/owner/email-alerts', { masterEmail: value });
    } catch (e: any) {
      setMaster(prev);
      toast.show({ type: 'error', title: 'Could not save', message: e?.message });
    } finally {
      setSaving(null);
    }
  };

  const saveKey = async (key: string, value: boolean) => {
    const prev = prefs[key];
    setPrefs(p => ({ ...p, [key]: value }));
    setSaving(key);
    try {
      await client.put('/api/owner/email-alerts', { prefs: { [key]: value } });
    } catch (e: any) {
      setPrefs(p => ({ ...p, [key]: prev }));
      toast.show({ type: 'error', title: 'Could not save', message: e?.message });
    } finally {
      setSaving(null);
    }
  };

  const groups = catalog.reduce<{ name: string; entries: CatalogEntry[] }[]>((acc, entry) => {
    const g = acc.find(x => x.name === entry.group);
    if (g) g.entries.push(entry); else acc.push({ name: entry.group, entries: [entry] });
    return acc;
  }, []);

  if (loading) {
    return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  }

  return (
    <ScrollView contentContainerStyle={styles.scroll}>
      <ContentContainer>
        <Text style={styles.title}>Email alerts</Text>
        <Text style={styles.subtitle}>
          Choose which alerts about your business arrive by email. Emails sent to your customers, your staff and your locations' support inboxes are not affected.
        </Text>
        {error && <Text style={styles.error}>{error}</Text>}

        <Card style={styles.section}>
          <View style={styles.row}>
            <View style={styles.rowText}>
              <Text style={styles.rowLabel}>Send me alert emails</Text>
              <Text style={styles.rowHint}>
                {master ? 'On. Turn off to pause every alert below.' : 'Off. No alert emails are sent until you turn this back on.'}
              </Text>
            </View>
            <Switch
              value={master}
              disabled={saving === 'master'}
              onValueChange={saveMaster}
              trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
              thumbColor={master ? colors.primary : colors.textLight}
            />
          </View>
        </Card>

        {groups.map(group => (
          <Card key={group.name} style={[styles.section, !master && styles.dimmed]}>
            <Text style={styles.sectionTitle}>{group.name}</Text>
            {group.entries.map((entry, i) => (
              <View key={entry.key} style={[styles.row, i > 0 && styles.rowBorder]}>
                <View style={styles.rowText}>
                  <Text style={styles.rowLabel}>{entry.label}</Text>
                  <Text style={styles.rowHint}>{entry.description}</Text>
                </View>
                <Switch
                  value={prefs[entry.key] !== false}
                  disabled={!master || saving === entry.key}
                  onValueChange={(v) => saveKey(entry.key, v)}
                  trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
                  thumbColor={prefs[entry.key] !== false ? colors.primary : colors.textLight}
                />
              </View>
            ))}
          </Card>
        ))}
      </ContentContainer>
    </ScrollView>
  );
}

const useStyles = (c: ColorTheme) => StyleSheet.create({
  scroll: { paddingBottom: spacing.xl },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs, marginBottom: spacing.md },
  error: { color: c.danger, marginBottom: spacing.sm },
  section: { marginBottom: spacing.md },
  dimmed: { opacity: 0.55 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: spacing.sm },
  rowBorder: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: c.borderLight },
  rowText: { flex: 1, paddingRight: spacing.md },
  rowLabel: { fontSize: fontSize.md, color: c.text },
  rowHint: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
});
